import { useState } from "react";
import {
  Box,
  Button,
  Card,
  CardActionArea,
  CardContent,
  Chip,
  Collapse,
  Link,
  TextField,
  Typography,
} from "@mui/material";
import PersonIcon from "@mui/icons-material/Person";

const getStyle = (type) => {
  if (type === "ACCEPT") {
    return {
      borderLeft: "6px solid #4caf50",
      badgeColor: "success",
      badgeLabel: "Lead",
    };
  }

  if (type === "NOT ACCEPT") {
    return {
      borderLeft: "6px solid #f44336",
      badgeColor: "error",
      badgeLabel: "Discard",
    };
  }

  return {
    borderLeft: "6px solid #fb8c00",
    badgeColor: "warning",
    badgeLabel: "Unsure",
  };
};

const getLeadTypeLabel = (type) => {
  if (type === "ACCEPT") return "Lead";
  if (type === "NOT ACCEPT") return "Discard";
  return "Unsure";
};

const hasMeaningfulValue = (value) =>
  value !== undefined &&
  value !== null &&
  String(value).trim() !== "" &&
  String(value).trim() !== "Unknown";

const getYearFromDate = (value) => {
  if (!value) return null;
  const date = new Date(value);
  if (!Number.isNaN(date.getTime())) {
    return date.getFullYear();
  }

  const year = parseInt(String(value).slice(0, 4), 10);
  return Number.isNaN(year) ? null : year;
};

export default function CompanyCard({
  company,
  feedbackData,
  setFeedbackData,
  onClassificationChange,
}) {
  const [expanded, setExpanded] = useState(false);
  const [showMoreDetails, setShowMoreDetails] = useState(false);
  const [pendingOverride, setPendingOverride] = useState(null);

  const companyId = company.id;
  const feedbackEntry = feedbackData[companyId];
  const existing = feedbackEntry || {
    comment: "",
    correctedType: company["Lead Type"],
    timestamp: null,
  };

  const leadType = existing.correctedType || company["Lead Type"];
  const originalLeadType = company["Original Lead Type"] || company["Lead Type"];
  const isManuallyOverridden = leadType !== originalLeadType;
  const leadTypeLabel = getLeadTypeLabel(leadType);
  const originalLeadTypeLabel = getLeadTypeLabel(originalLeadType);
  const style = getStyle(leadType);
  const companyName = company["Organization Name"];
  const foundedYear = getYearFromDate(company["Founded"]);
  const lastFundingYear = getYearFromDate(company["Last Funding Date"]);

  const coverageItems = [
    { label: "Founded", value: company["Founded"] },
    { label: "Funding Date", value: company["Last Funding Date"] },
    { label: "Stage", value: company["Investment Stage"] },
    { label: "Revenue", value: company["Estimated Revenue Range"] },
    { label: "Website", value: company["Website"] },
    { label: "Crunchbase", value: company["Crunchbase"] },
  ];

  const coverageCount = coverageItems.filter((item) => hasMeaningfulValue(item.value)).length;
  const coveragePercent = Math.round((coverageCount / coverageItems.length) * 100);

  const updateFeedback = (updates) => {
    const newData = {
      ...existing,
      ...updates,
      timestamp: new Date().toISOString(),
    };

    setFeedbackData((prev) => ({
      ...prev,
      [companyId]: newData,
    }));
  };

  const requestOverride = (nextType) => {
    if (pendingOverride === nextType) {
      updateFeedback({ correctedType: nextType });
      onClassificationChange?.(companyName, nextType);
      setPendingOverride(null);
      return;
    }

    setPendingOverride(nextType);
  };

  const handleCardToggle = () => {
    setExpanded((prev) => {
      const nextExpanded = !prev;
      if (!nextExpanded) {
        setPendingOverride(null);
        setShowMoreDetails(false);
      }
      return nextExpanded;
    });
  };

  return (
    <Card
      variant="outlined"
      sx={{
        marginBottom: 2,
        borderRadius: 3,
        borderLeft: style.borderLeft,
        transition: "all 0.25s ease",
        boxShadow: expanded
          ? "0px 4px 14px rgba(0,0,0,0.18)"
          : "0px 2px 6px rgba(0,0,0,0.1)",
        ":hover": {
          boxShadow: "0px 6px 18px rgba(0,0,0,0.2)",
          transform: "scale(1.01)",
        },
      }}
    >
      <CardActionArea onClick={handleCardToggle}>
        <CardContent sx={{ position: "relative" }}>
          {isManuallyOverridden && (
            <Chip
              icon={<PersonIcon />}
              label="Manual Change"
              color="info"
              size="small"
              sx={{
                position: "absolute",
                top: 10,
                right: 96,
                opacity: expanded ? 1 : 0.9,
                transition: "opacity 0.2s ease-in-out",
              }}
            />
          )}

          <Chip
            label={style.badgeLabel}
            color={style.badgeColor}
            size="small"
            sx={{
              position: "absolute",
              top: 10,
              right: 10,
              opacity: expanded ? 1 : 0.9,
              transition: "opacity 0.2s ease-in-out",
            }}
          />

          <Typography variant="h6" sx={{ fontWeight: 700 }}>
            {companyName}
          </Typography>

          <Typography
            variant="body1"
            sx={{
              fontStyle: "italic",
              marginTop: 1,
              fontSize: "1rem",
            }}
          >
            {company["Reasoning"]}
          </Typography>
        </CardContent>
      </CardActionArea>

      <Collapse in={expanded} timeout={{ enter: 400, exit: 250 }} unmountOnExit>
        <CardContent sx={{ backgroundColor: "#fafafa" }}>
          <Typography sx={{ mb: 1 }}>
            <strong>Description:</strong> {company["Description"]}
          </Typography>

          <Typography sx={{ mb: 1 }}>
            <strong>Industries:</strong> {company["Industries"]}
          </Typography>

          <Box sx={{ display: "grid", gap: 0.5, mb: 1 }}>
            <Typography>
              <strong>Headquarters:</strong> {company["Headquarters Location"]}
            </Typography>
            <Typography>
              <strong>Employees:</strong> {company["Employees"]}
            </Typography>
            <Typography>
              <strong>Investment Stage:</strong> {company["Investment Stage"]}
            </Typography>
            <Typography>
              <strong>Last Funding Date:</strong> {company["Last Funding Date"]}
            </Typography>
          </Box>

          <Typography sx={{ mb: 1 }}>
            <strong>Justification:</strong> {company["Reasoning"]}
          </Typography>

          <Typography sx={{ mb: 1 }}>
            <strong>Classification:</strong>
          </Typography>

          {isManuallyOverridden && (
            <Typography sx={{ mb: 1, fontSize: "0.9rem", color: "#475569" }}>
              Manually overridden from {originalLeadTypeLabel} to {leadTypeLabel}.
            </Typography>
          )}

          {pendingOverride && (
            <Typography sx={{ mb: 1, fontSize: "0.9rem", color: "#b45309" }}>
              Click {getLeadTypeLabel(pendingOverride)} again to confirm this override.
            </Typography>
          )}

          <Box sx={{ display: "flex", gap: 2, mb: 2, flexWrap: "wrap" }}>
            <Button
              variant={leadType === "ACCEPT" ? "contained" : "outlined"}
              color="success"
              onClick={() => requestOverride("ACCEPT")}
            >
              Lead
            </Button>

            <Button
              variant={leadType === "NOT ACCEPT" ? "contained" : "outlined"}
              color="error"
              onClick={() => requestOverride("NOT ACCEPT")}
            >
              Discard
            </Button>

            <Button
              variant={leadType === "FUZZY" ? "contained" : "outlined"}
              color="warning"
              onClick={() => requestOverride("FUZZY")}
            >
              Unsure
            </Button>
          </Box>

          <Box sx={{ mt: 1 }}>
            <TextField
              label="Notes"
              multiline
              fullWidth
              rows={3}
              value={existing.comment}
              onChange={(e) => updateFeedback({ comment: e.target.value })}
            />
          </Box>

          {existing.timestamp && (
            <Typography sx={{ mt: 1, fontSize: "0.8rem", color: "#666" }}>
              Last updated: {new Date(existing.timestamp).toLocaleString()}
            </Typography>
          )}

          <Box sx={{ mt: 2 }}>
            <Button
              variant="text"
              onClick={() => setShowMoreDetails((prev) => !prev)}
              sx={{ textTransform: "none", px: 0, fontWeight: 700 }}
            >
              {showMoreDetails ? "Hide More Details" : "More Details"}
            </Button>
          </Box>

          <Collapse in={showMoreDetails} timeout={{ enter: 250, exit: 200 }} unmountOnExit>
            <Box
              sx={{
                mt: 1.5,
                pt: 1.5,
                borderTop: "1px solid rgba(15, 23, 42, 0.08)",
                display: "grid",
                gap: 1,
              }}
            >
              <Box
                sx={{
                  border: "1px solid rgba(15, 23, 42, 0.08)",
                  borderRadius: 2,
                  backgroundColor: "#ffffff",
                  p: 1.25,
                  display: "grid",
                  gap: 1,
                }}
              >
                <Typography sx={{ fontWeight: 700 }}>Data Coverage</Typography>
                <Box
                  sx={{
                    height: 10,
                    borderRadius: 999,
                    backgroundColor: "#e2e8f0",
                    overflow: "hidden",
                  }}
                >
                  <Box
                    sx={{
                      width: `${coveragePercent}%`,
                      height: "100%",
                      background: "linear-gradient(90deg, #2dd4bf 0%, #0f766e 100%)",
                    }}
                  />
                </Box>
                <Typography sx={{ fontSize: "0.85rem", color: "#475569" }}>
                  {coverageCount} of {coverageItems.length} key company fields available
                </Typography>
                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
                    gap: 0.75,
                  }}
                >
                  {coverageItems.map((item) => {
                    const available = hasMeaningfulValue(item.value);
                    return (
                      <Box
                        key={item.label}
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 0.75,
                          fontSize: "0.84rem",
                          color: available ? "#0f172a" : "#94a3b8",
                        }}
                      >
                        <Box
                          sx={{
                            width: 10,
                            height: 10,
                            borderRadius: "50%",
                            backgroundColor: available ? "#14b8a6" : "#cbd5e1",
                            flexShrink: 0,
                          }}
                        />
                        <Typography sx={{ fontSize: "0.84rem" }}>{item.label}</Typography>
                      </Box>
                    );
                  })}
                </Box>
              </Box>

              <Box
                sx={{
                  border: "1px solid rgba(15, 23, 42, 0.08)",
                  borderRadius: 2,
                  backgroundColor: "#ffffff",
                  p: 1.25,
                  display: "grid",
                  gap: 1,
                }}
              >
                <Typography sx={{ fontWeight: 700 }}>Company Timeline</Typography>
                {foundedYear && lastFundingYear ? (
                  <>
                    <Box sx={{ position: "relative", pt: 2, pb: 1 }}>
                      <Box
                        sx={{
                          position: "absolute",
                          top: 10,
                          left: 0,
                          right: 0,
                          height: 4,
                          borderRadius: 999,
                          backgroundColor: "#dbeafe",
                        }}
                      />
                      <Box
                        sx={{
                          position: "relative",
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "flex-start",
                        }}
                      >
                        {[
                          { label: "Founded", year: foundedYear, color: "#2563eb" },
                          {
                            label: "Last Funding",
                            year: lastFundingYear,
                            color: "#0f766e",
                          },
                        ].map((point) => (
                          <Box
                            key={`${point.label}-${point.year}`}
                            sx={{
                              display: "grid",
                              justifyItems: "center",
                              gap: 0.5,
                              minWidth: 90,
                            }}
                          >
                            <Box
                              sx={{
                                width: 14,
                                height: 14,
                                borderRadius: "50%",
                                backgroundColor: point.color,
                                border: "3px solid #ffffff",
                                boxShadow: "0 0 0 1px rgba(15, 23, 42, 0.08)",
                                zIndex: 1,
                              }}
                            />
                            <Typography sx={{ fontSize: "0.82rem", fontWeight: 700 }}>
                              {point.label}
                            </Typography>
                            <Typography sx={{ fontSize: "0.82rem", color: "#475569" }}>
                              {point.year}
                            </Typography>
                          </Box>
                        ))}
                      </Box>
                    </Box>
                    <Typography sx={{ fontSize: "0.85rem", color: "#475569" }}>
                      {Math.max(lastFundingYear - foundedYear, 0)} years from founding to latest funding event
                    </Typography>
                  </>
                ) : (
                  <Typography sx={{ fontSize: "0.85rem", color: "#64748b" }}>
                    Timeline unavailable because one or more dates are missing.
                  </Typography>
                )}
              </Box>

              <Typography>
                <strong>Founded:</strong> {company["Founded"]}
              </Typography>
              <Typography>
                <strong>Crunchbase Rank:</strong> {company["Crunchbase Rank"]}
              </Typography>
              <Typography>
                <strong>Estimated Revenue Range:</strong>{" "}
                {company["Estimated Revenue Range"] || "Unknown"}
              </Typography>
              <Typography>
                <strong>Investor Type:</strong> {company["Investor Type"] || "Unknown"}
              </Typography>
              <Typography>
                <strong>Original Lead Type:</strong> {originalLeadTypeLabel}
              </Typography>
              <Typography>
                <strong>Website:</strong>{" "}
                <Link href={company["Website"]} target="_blank">
                  {company["Website"]}
                </Link>
              </Typography>
              <Typography>
                <strong>Crunchbase:</strong>{" "}
                <Link href={company["Crunchbase"]} target="_blank">
                  {company["Crunchbase"]}
                </Link>
              </Typography>
            </Box>
          </Collapse>
        </CardContent>
      </Collapse>
    </Card>
  );
}
